import * as React from 'react';
// mui
import {
  Button,
  Divider,
  DialogContent,
  TextField,
  DialogActions,
  Slider,
  Grid,
  Dialog,
  DialogTitle,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  LocalizationProvider,
  DatePicker
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// icons
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import FlagIcon from '@mui/icons-material/Flag';
// redux
import { useSelector } from 'react-redux';
import { selectTaskById } from '../../../store/selectors/taskSelectors';
import { selectAllProjects, selectSelectedProjectId } from '../../../store/selectors/projectSelectors';
// dayjs
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
dayjs.extend(customParseFormat);
import 'dayjs/locale/en-gb';
// types
import Task from '../../../interfaces/Task.interface';
import Project from '../../../interfaces/Project.interface';
// axios
import api from '../../../axios';
// components
import { desktopSliderMarks, mobileSliderMarks } from '../../common/sliderMarks';
import ItemBreadcrumbs from '../../common/ItemBreadcrumbs';

const emptyTaskData: Task = {
  id: 0,
  title: '',
  description: '',
  energy: 3,
  time: 2,
  priority: 3,
  dueDate: null,
  parentTaskId: null,
  projectId: null,
  completed: false,
  createdDate: null,
  updatedDate: null,
  completedDate: null,
  excused: false,
  excuseExpiresAt: null,
  subtaskIds: [],
  miniTasks: [],
}

interface TaskDialogProps {
  onSave: (task: Task) => void;
  onCancel: () => void;
  addMode: Boolean;
  parentTaskId: number | null;
  taskId: number | null; // can be null if dialog in add mode
}

export default function TaskDialog(props: TaskDialogProps) {
  const { onSave, onCancel, addMode, parentTaskId, taskId } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // redux
  const selectParentTask = parentTaskId
    ? selectTaskById(parentTaskId)
    : () => null;
  const parentTaskRaw = useSelector(selectParentTask);
  const parentTaskData: Task | null = parentTaskRaw ?? null;

  const selectTask = taskId
    ? selectTaskById(taskId)
    : () => null;
  const taskRaw = useSelector(selectTask);
  const originalTaskData: Task = taskRaw ?? emptyTaskData;

  const localProjects: Project[] = useSelector(selectAllProjects());
  const localSelectedProjectId: number | null = useSelector(selectSelectedProjectId());

  let projectIdToUse: number | null = 0;
  if (parentTaskData) { // first try parent project
    projectIdToUse = parentTaskData.projectId;
  } else if (addMode) { // second try selected project
    projectIdToUse = localSelectedProjectId;
  } else { // thirdly try existing project (generally the case for parentless tasks and will default null)
    projectIdToUse = originalTaskData.projectId;
  }
  const [ taskData, setTaskData ] = React.useState<Task>({
    ...originalTaskData,
    parentTaskId: parentTaskId,
    projectId: projectIdToUse === null ? 0 : projectIdToUse // shift null to 0 for use in select component
  });
  const [ open, setOpen ] = React.useState(true); // by default open the modal as it is conditionally rendered in parent
  const [ loading, setLoading ] = React.useState(false);

  const handleDialogClose = () => {
    onCancel();
    setOpen(false);
    setTaskData(originalTaskData);
  }

  const buildChildIdList = (tasks: Task[], currentTaskId: number = taskData.id, childIdList: number[] = []): number[] => {
    if (tasks.length > 0) {
      for (let i = 0; i < tasks.length; i += 1) {
        if (tasks[i].id == currentTaskId) {
          childIdList.push(tasks[i].id);
          const subtasks: number[] = tasks[i].subtaskIds;
          for (let n = 0; n < subtasks.length; n += 1) {
            childIdList = buildChildIdList(tasks, subtasks[n], childIdList);
          }
        }
      }

      return childIdList;
    } else {
      return [];
    }
  }

  const handleDialogSave = () => {
    setLoading(true);
    
    let saveData: Task = {...taskData};

    // local components use 0 for null, replace this before posting to API
    if (saveData.projectId === 0) {
      saveData.projectId = null;
    }

    if (addMode) {
      if (typeof parentTaskId === 'number') {
        saveData = {
          ...saveData,
          parentTaskId: parentTaskId
        };
      }

      api.post(`/tasks/normalized/create`, saveData)
        .then((res) => {
          console.log(res);

          setOpen(false);
          setTaskData(originalTaskData);
          setLoading(false);

          onSave(saveData);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    } else {
      api.put(`/tasks/normalized/update/${saveData.id}`, saveData)
        .then((res) => {
          console.log(res);

          setOpen(false);
          setTaskData(originalTaskData);
          setLoading(false);

          onSave(saveData);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    }
  }

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      onClose={handleDialogClose}
      fullWidth
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1 }}>
        <ItemBreadcrumbs
          currentItem={taskData}
          itemType="task"
          includeCurrentItem={false}
        />
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: isMobile ? 2 : '' }}>
        <Grid sx={{ mb: 2 }} container spacing={1}>
          <Grid size={12} >
            <TextField
              value={taskData.title}
              onChange={(e) => { setTaskData({
                ...taskData,
                title: e.target.value
              }); }}
              sx={{ mt: 0 }}
              autoFocus
              required
              margin="dense"
              id="name"
              label="Title"
              fullWidth
              variant="standard"
            />
          </Grid>
        </Grid>

        <Grid sx={{ mb: 2 }} container spacing={1}>
          <Grid size={7} >
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Project</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={taskData.projectId ? taskData.projectId : 0}
                label="Project"
                disabled={parentTaskId !== null}
                onChange={(e) => {
                  setTaskData({
                    ...taskData,
                    projectId: Number(e.target.value)
                  });
                }}
              >
                { localProjects.map((project: Project) => (
                  <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={5}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="YYYY/MM/DD"
                slotProps={{
                  actionBar: {
                    actions: ['clear', 'cancel', 'accept'], // enables clear button
                  },
                }}
                value={taskData.dueDate ? dayjs.utc(taskData.dueDate).local() : null}
                onChange={(val) => {
                  if (val && val.isValid()) {
                    setTaskData({
                      ...taskData,
                      dueDate: val.utc().toISOString(),
                    });
                  } else {
                    setTaskData({
                      ...taskData,
                      dueDate: null,
                    });
                  }
                }}
                label="Due Date"
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
        
        <TextField
          label="Description"
          value={taskData.description}
          onChange={(e) => {
            setTaskData({
              ...taskData,
              description: e.target.value
            });
          }}
          sx={{ paddingBottom: 1 }}
          multiline
          rows={4}
          fullWidth
        />
        <Grid container spacing={1} sx={{ paddingX: isMobile ? 2 : 3, pl: 0, paddingY: 1, width: '100%' }}>
          <Grid size={1.5}>
            <ElectricBoltIcon sx={{ mt: isMobile ? 1.5 : 0.5 }} />
          </Grid>
          <Grid size={10.5}>
            <Slider
              aria-label="Energy"
              value={taskData.energy}
              onChange={(e: any) => {
                setTaskData({
                  ...taskData,
                  energy: e.target.value
                });
              }}
              defaultValue={1}
              min={1}
              max={5}
              step={1}
              valueLabelDisplay="auto"
              marks={isMobile ? mobileSliderMarks.energy : desktopSliderMarks.energy}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} sx={{ paddingX: isMobile ? 2 : 3, pl: 0, paddingY: 1, width: '100%' }}>
          <Grid size={1.5}>
            <FlagIcon sx={{ mt: isMobile ? 1.5 : 0.5 }} />
          </Grid>
          <Grid size={10.5}>
            <Slider
              aria-label="Priority"
              value={taskData.priority}
              onChange={(e: any) => {
                setTaskData({
                  ...taskData,
                  priority: e.target.value
                });
              }}
              defaultValue={1}
              min={1}
              max={5}
              step={1}
              valueLabelDisplay="auto"
              marks={isMobile ? mobileSliderMarks.priority : desktopSliderMarks.priority}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} sx={{ paddingX: isMobile ? 2 : 3, pl: 0, paddingY: 1, width: '100%' }}>
          <Grid size={1.5}>
            <AccessAlarmIcon sx={{ mt: isMobile ? 1.5 : 0.5 }} />
          </Grid>
          <Grid size={10.5}>
            <Slider
              aria-label="Time"
              value={taskData.time}
              onChange={(e: any) => {
                setTaskData({
                  ...taskData,
                  time: e.target.value
                });
              }}
              defaultValue={2}
              min={1}
              max={5}
              step={1}
              valueLabelDisplay="auto"
              marks={isMobile ? mobileSliderMarks.time : desktopSliderMarks.time}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleDialogClose}>Cancel</Button>
        <Button
          variant="contained"
          loading={loading}
          onClick={(e) => { e.preventDefault(); handleDialogSave() }}
          disabled={!taskData.title || (taskData.dueDate === '' && !dayjs(taskData.dueDate).isValid())}
        >{addMode ? 'Create' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  )
}